import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from 'react';
import { fecharCofre } from '../lib/cofre';
import type { Session } from '@supabase/supabase-js';
import { supabase } from '../lib/supabase';
import { DEMO } from '../lib/env';
import { initStore, clearStore, syncNow, temPendente } from '../store/store';
import { emailDoCpf, cpfDoEmail, loginDe, soDigitos } from '../lib/cpf';
import { t } from '../lib/i18n';
import { setContaCriadaEm } from '../lib/plano';
import { Alert } from 'react-native';
import { apagarChaves } from '../lib/iaChave';
import { aindaConectado, computadorAtual, desconectarComputador, registrarComputador } from '../lib/computador';

// My Planner Web. Entra pelo QR Code lido no app do celular (ou com CPF e senha).
// Mesma interface do AuthContext do celular; no navegador não há bloqueio por biometria.
// Na prévia de demonstração (EXPO_PUBLIC_DEMO=1) o login é fictício e sem servidor.
type Status = 'loading' | 'signedOut' | 'locked' | 'unlocked';
type Ctx = {
  status: Status; cpf: string | null; email: string | null; nome: string; bio: boolean; bioDisponivel: boolean;
  signIn: (cpf: string, senha: string) => Promise<void>;
  signUp: (nome: string, cpf: string, email: string, senha: string) => Promise<'ok' | 'confirmar'>;
  unlockSenha: (senha: string) => Promise<boolean>;
  conferirSenha: (senha: string) => Promise<boolean>;
  unlockBio: () => Promise<boolean>;
  setBio: (on: boolean) => Promise<boolean>;
  lock: () => void;
  signOut: (forcar?: boolean) => Promise<void>;   // forcar: sai sem perguntar sobre alterações não enviadas
  /** Sessão do servidor venceu: confirma a senha e volta a sincronizar, sem apagar nada deste aparelho. */
  reentrar: (senha: string) => Promise<void>;
  deleteAccount: () => Promise<void>;
  entrarComToken?: (tokenHash: string) => Promise<void>;
  trocarSenha: (atual: string, nova: string) => Promise<void>;
  trocarEmail: (email: string) => Promise<void>;
  padrao: boolean;
  setPadrao: (seq: string | null) => Promise<void>;
  unlockPadrao: (seq: string) => Promise<boolean>;
  bioTipos: string[];
};
// No navegador não há bloqueio local: desenho padrão e biometria ficam só no celular.
const SEM_BLOQUEIO = { padrao: false, setPadrao: async () => {}, unlockPadrao: async () => false, bioTipos: [] as string[],
  pin: false, setPin: async () => {}, unlockPin: async () => false, avisarRosto: () => {} };
const AuthCtx = createContext<Ctx | null>(null);
export const semBloqueio = <T,>(fn: () => Promise<T>) => fn();

function DemoProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<Status>('signedOut');
  const [cpf, setCpf] = useState<string | null>(null);
  const [email, setEmail] = useState<string | null>(null);
  const [nome, setNome] = useState('');
  const entrar = useCallback(async (c: string, n: string, e: string | null) => {
    await initStore('demo'); setCpf(c.replace(/\D/g, '')); setEmail(e); setNome(n || t('Demonstração')); setStatus('unlocked');
  }, []);
  const v: Ctx = {
    status, cpf, email, nome, bio: false, bioDisponivel: false,
    signIn: (c) => entrar(c, '', null),
    signUp: async (n, c, e) => { await entrar(c, n, e || null); return 'ok'; },
    unlockSenha: async () => { setStatus('unlocked'); return true; },
    conferirSenha: async (s) => !!s,
    unlockBio: async () => false,
    setBio: async () => false,
    lock: () => setStatus('locked'),
    signOut: async () => setStatus('signedOut'),
    reentrar: async () => {},
    deleteAccount: async () => setStatus('signedOut'),
    trocarSenha: async () => {},
    trocarEmail: async (e) => { setEmail(e.trim().toLowerCase() || null); },
    ...SEM_BLOQUEIO,
  };
  return <AuthCtx.Provider value={v}>{children}</AuthCtx.Provider>;
}

function WebProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<Status>('loading');
  const [session, setSession] = useState<Session | null>(null);
  const carregado = useRef<string | null>(null);

  const entrarCom = useCallback(async (s: Session, entrouAgora = false) => {
    // texto 12 C: só um computador por vez. Entrando agora (QR Code ou senha): se já houver outro computador, pergunta se troca.
    // Abrindo de novo a página: se este navegador foi desconectado (pelo celular ou por outro computador), sai.
    if (entrouAgora) {
      if (await registrarComputador(false) === 'outro') {
        const trocar = await perguntar(t('Já existe um computador conectado'), t('Já existe um computador conectado. Trocar para este? O outro será desconectado.'), t('Trocar'));
        if (!trocar) {
          await supabase?.auth.signOut({ scope: 'local' }).catch(() => {}); setStatus('signedOut');
          throw new Error(t('Entrada cancelada. Este computador não foi conectado.'));   // a tela de entrada volta a mostrar o QR Code
        }
        await registrarComputador(true);
      }
    } else {
      const atual = await computadorAtual();
      if (atual && !(await aindaConectado())) {
        await supabase?.auth.signOut({ scope: 'local' }).catch(() => {});
        setStatus('signedOut');
        return;
      }
      if (atual === null) await registrarComputador(false);
    }
    setSession(s);
    setContaCriadaEm(s.user.created_at);
    if (carregado.current !== s.user.id) { await initStore(s.user.id); carregado.current = s.user.id; }
    setStatus('unlocked');
  }, []);

  useEffect(() => {
    (async () => {
      if (!supabase) { setStatus('signedOut'); return; }
      try {
        const { data } = await supabase.auth.getSession();
        if (data.session) await entrarCom(data.session); else setStatus('signedOut');
      } catch { setStatus(st => (st === 'loading' ? 'signedOut' : st)); }   // nunca fica preso no "carregando"
    })();
    const sub = supabase?.auth.onAuthStateChange((ev, s) => {
      if (ev === 'TOKEN_REFRESHED' && s) setSession(s);
      if (ev === 'SIGNED_OUT') { carregado.current = null; setSession(null); setStatus('signedOut'); clearStore().catch(() => {}); apagarChaves().catch(() => {}); }
    });
    return () => sub?.data.subscription.unsubscribe();
  }, [entrarCom]);

  const signIn = useCallback(async (cpf: string, senha: string) => {
    if (!supabase) throw new Error(t('Servidor não configurado.'));
    const { data, error } = await supabase.auth.signInWithPassword({ email: loginDe(cpf), password: senha });
    if (error) throw error;
    await entrarCom(data.session, true);
  }, [entrarCom]);

  const signUp = useCallback(async (nome: string, cpf: string, email: string, senha: string) => {
    if (!supabase) throw new Error(t('Servidor não configurado.'));
    const { data, error } = await supabase.auth.signUp({ email: soDigitos(cpf) ? emailDoCpf(cpf) : email.trim().toLowerCase(), password: senha,
      options: { data: { nome: nome.trim(), cpf: soDigitos(cpf) || null, email_contato: email.trim().toLowerCase() || null, app: 'myplanner' } } });
    if (error) throw error;
    if (!data.session) return 'confirmar';
    await entrarCom(data.session, true);
    return 'ok';
  }, [entrarCom]);

  const entrarComToken = useCallback(async (tokenHash: string) => {
    if (!supabase) throw new Error(t('Servidor não configurado.'));
    let r = await supabase.auth.verifyOtp({ token_hash: tokenHash, type: 'magiclink' });
    if (r.error) r = await supabase.auth.verifyOtp({ token_hash: tokenHash, type: 'email' });
    if (r.error || !r.data.session) throw r.error ?? new Error(t('Não foi possível entrar.'));
    await entrarCom(r.data.session, true);
  }, [entrarCom]);

  const trocarSenha = useCallback(async (atual: string, nova: string) => {
    if (!supabase || !session?.user.email) throw new Error(t('Servidor não configurado.'));
    const conf = await supabase.auth.signInWithPassword({ email: session.user.email, password: atual });
    if (conf.error) throw new Error(t('A senha atual não confere.'));
    const { error } = await supabase.auth.updateUser({ password: nova });
    if (error) throw error;
  }, [session]);

  const trocarEmail = useCallback(async (email: string) => {
    if (!supabase || !session) throw new Error(t('Servidor não configurado.'));
    const e = email.trim().toLowerCase() || null;
    const { data, error } = await supabase.auth.updateUser({ data: { email_contato: e } });
    if (error) throw error;
    await supabase.from('perfis').update({ email_contato: e }).eq('id', session.user.id);
    if (data.user) setSession(s => (s ? { ...s, user: data.user } : s));
  }, [session]);

  const reentrar = useCallback(async (senha: string) => {
    if (!supabase || !session?.user.email) throw new Error(t('Servidor não configurado.'));
    const { data, error } = await supabase.auth.signInWithPassword({ email: session.user.email, password: senha });
    if (error) throw new Error(/invalid/i.test(error.message) ? t('Senha incorreta.') : error.message);
    if (data.session.user.id !== session.user.id) throw new Error(t('Esta senha é de outra conta.'));
    setSession(data.session);
    await syncNow();
  }, [session]);

  const conferirSenha = useCallback(async (senha: string) => {
    if (!senha || !supabase || !session?.user.email) return false;
    const { data, error } = await supabase.auth.signInWithPassword({ email: session.user.email, password: senha });
    if (error || data.session?.user.id !== session.user.id) return false;
    setSession(data.session);
    return true;
  }, [session]);

  const sair = useCallback(async () => {
    await clearStore();
    await apagarChaves();
    carregado.current = null;
    setSession(null); setStatus('signedOut');
  }, []);

  const signOut = useCallback(async (forcar = false) => {
    await syncNow().catch(() => {});
    if (!forcar && temPendente() && !(await perguntar(t('Alterações ainda não enviadas'), t('Algumas alterações ainda não foram enviadas para o servidor (sem internet?). Se sair agora, elas se perdem. Sair mesmo assim?'), t('Sair mesmo assim')))) return;
    // texto 12 C: "Sair deste computador" libera a vaga (só se este navegador ainda for o computador da conta)
    if (await aindaConectado()) await desconectarComputador().catch(() => {});
    fecharCofre();
    await supabase?.auth.signOut({ scope: 'local' }).catch(() => {});   // só este navegador; o celular continua
    await sair();
  }, [sair]);

  const deleteAccount = useCallback(async () => {
    if (!supabase) return;
    const { error } = await supabase.rpc('apagar_minha_conta');
    if (error) throw error;
    await supabase.auth.signOut({ scope: 'local' }).catch(() => {});
    await sair();
  }, [sair]);

  const meta = session?.user.user_metadata ?? {};
  const v: Ctx = {
    status, nome: String(meta.nome ?? ''), bio: false, bioDisponivel: false,
    cpf: (meta.cpf as string | undefined) ?? cpfDoEmail(session?.user.email),
    email: (meta.email_contato as string | undefined) ?? null,
    signIn, signUp, entrarComToken, signOut, reentrar, deleteAccount, trocarSenha, trocarEmail, conferirSenha, ...SEM_BLOQUEIO,
    // texto 12 C: bloqueio automático do computador (tempo parado): desbloqueia com a senha da conta (ou entra com o QR Code de novo)
    unlockSenha: async (senha: string) => { const ok = await conferirSenha(senha); if (ok) setStatus('unlocked'); return ok; },
    unlockBio: async () => false,
    setBio: async () => false,
    lock: () => setStatus(st => (st === 'unlocked' ? 'locked' : st)),
  };
  return <AuthCtx.Provider value={v}>{children}</AuthCtx.Provider>;
}

/** Pergunta com Sim/Não que espera a resposta (no navegador o Alert vira uma janela do app). */
const perguntar = (titulo: string, texto: string, sim: string) => new Promise<boolean>(res =>
  Alert.alert(titulo, texto, [{ text: t('Cancelar'), style: 'cancel', onPress: () => res(false) }, { text: sim, onPress: () => res(true) }], { onDismiss: () => res(false) }));

export const AuthProvider = DEMO ? DemoProvider : WebProvider;

export function useAuth() {
  const c = useContext(AuthCtx);
  if (!c) throw new Error('useAuth fora do AuthProvider');
  return c;
}
